// Edit team: my own team's name and colors (SCENARIOS L2).
//
// A manager can change their team's name and colors whenever they like. Until
// this sheet, a team got its name and colors once, on joining, and never again.
//
// ONE QUESTION: what should my team look like? So the sheet is the team as it
// will look — the badge and the name on the tint a matchup card gives them —
// and the ways to change it under it: the name, then three ways to the colors.
// The twenty house pairs, each drawn as the badge it would make in the letters
// the name would give, so choosing is looking rather than imagining; "Match a
// pro team"; and **Custom**, any two colors of your own. There is more than NFL
// team colors because some people have no favorite team and want to pick
// their own.
//
// CUSTOM IS NOT CHECKED FOR CONTRAST WITH THE PAGE. The initials are drawn in
// `onColor(primary)` whatever the fill, so they stay readable on the badge; how
// the badge sits on a card is what the preview shows, and it is their team. A
// half-typed or malformed hex never reaches Save: the field says what is wrong
// and Save waits.
//
// WHICH CHOICE READS AS CHOSEN: the house swatch when the pair is a house pair,
// the pro team when it is one's, and Custom — outlined, with the colors in its
// fields — when it is neither.
//
// AND ITS BANNER (PLAN-2026-10-24-team-banners §4), behind the preview so it
// is seen while it is picked, with its own section under the badge.
//
// NO NUMBERS, so it is the same sheet at every detail level; Simple has nothing
// here to remove. Save sends the change and keeps a refusal inside the sheet in
// the server's own words; success closes it and every screen that shows the
// team reads it again (`data/team.ts`).

import { useRef, useState } from 'react';
import { Icon } from '../../kit/Icon';
import {
  Button,
  Choice,
  ColorField,
  Colored,
  Line,
  Stack,
  Text,
  TextField,
  tell,
} from '../../kit/ui';
import {
  HOUSE_COLORS,
  badgeFromFile,
  chosenBadge,
  sameBadge,
  sameColors,
  teamAbbreviation,
  type ChosenBadge,
  type TeamBannerId,
  type TeamColors,
} from '@fsp/domain';
import { readLeagueFile } from '../../data/importFile';
import type { SportDefinition } from '@fsp/sports-core';
import { TeamMark } from '../../kit';
import { TeamFigure, teamFace } from '../../kit/TeamFigure';
import { TeamBanner, hasBanner } from '../../kit/TeamBanner';
import { useFaceFavorites } from '../../data/faceFavorites';
import { DetailSheet } from '../../templates/DetailSheet';
import { getSport } from '../../sports/registry';
import { useMyTeamHere, useSaveTeam, type MyTeamHere } from '../../data/team';
import { hexOf } from '../../data/hex';
import { BadgePicker } from './BadgePicker';
import { BannerPicker } from './BannerPicker';
import { CoManagers } from './CoManagers';
import { FaceMaker, type StartingFace } from './FaceMaker';
import { useLeagueCards } from '../../data/LeaguesProvider';

/** The sport's definition, or null for one this build does not know — then there is no pro list to offer. */
function sportOf(id: string): SportDefinition | null {
  try {
    return getSport(id as never);
  } catch {
    return null;
  }
}

const HEX_HINT = 'Six hex digits, like #2563EB.';

export function EditTeamSheet({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const here = useMyTeamHere();
  // Closed, or nothing of mine on screen: the drawer is still mounted closed,
  // so opening it animates in rather than appearing.
  if (!opened || here === null) {
    return (
      <DetailSheet opened={false} onClose={onClose} title="Edit team">
        {null}
      </DetailSheet>
    );
  }
  // KEYED ON THE TEAM, so a sheet opened on another league's team starts from
  // that team rather than from what was typed for the last one.
  return <EditTeamBody key={`${here.leagueId}|${here.team.id}`} here={here} onClose={onClose} />;
}

function EditTeamBody({ here, onClose }: { here: MyTeamHere; onClose: () => void }) {
  const save = useSaveTeam();
  const sport = sportOf(here.sport);
  const [name, setName] = useState(here.team.name);
  const [colors, setColors] = useState<TeamColors>(here.team.colors);
  // THE BADGE (B-440): a real league's; the practice league's teams wear initials.
  // A bot's face is not a person's to keep or send (B-464): it reads as none.
  const [badge, setBadge] = useState<ChosenBadge>(chosenBadge(here.team.badge ?? null));
  const badgeChanged = here.source === 'api' && !sameBadge(badge, here.team.badge ?? null);
  // THE BANNER, a real league's as the badge is. What the account has earned
  // comes with its favorite faces, which the face maker reads as well.
  const kept = here.team.banner ?? null;
  const [banner, setBanner] = useState<TeamBannerId | null>(kept);
  const bannerChanged = here.source === 'api' && banner !== kept;
  // NULL UNTIL READ: until then, or if it cannot be, the picker locks nothing
  // rather than telling somebody who made the playoffs they have not.
  const favorites = useFaceFavorites();
  const earned = favorites.faces === null ? null : favorites.earned;
  // THE FACE MAKER (B-488), over the sheet. Its Done puts the face here, with
  // the colors its paint can gave the disc, and keeps them at once.
  const [making, setMaking] = useState(false);
  const { cards } = useLeagueCards();
  const others: StartingFace[] = cards.flatMap((c) =>
    c.league.id !== here.leagueId && c.myTeam?.badge?.kind === 'face'
      ? [{ name: c.myTeam.name, face: c.myTeam.badge.face }]
      : [],
  );
  // What the Custom fields hold as typed, which may not be a color yet. They
  // follow every other choice, so Custom always starts from what the team wears.
  const [custom, setCustom] = useState<TeamColors>(here.team.colors);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // A COLOR PICKER OWNS ESCAPE WHILE IT IS OPEN. Its dropdown is open exactly
  // while its field has focus (the kit's ColorField), so Escape there closes
  // the picker — by leaving the field — and not the sheet with the edit in it;
  // the next Escape closes the sheet as usual.
  const [picking, setPicking] = useState(false);

  const choose = (next: TeamColors) => {
    setColors(next);
    setCustom(next);
  };

  // A TEAM'S FILE (A13, H10): its name, colors and badge filled in, for the
  // person to look at and save. The open league format's own team, by key.
  const fileInput = useRef<HTMLInputElement>(null);
  const fromFile = async (picked: File | undefined) => {
    if (!picked) return;
    const read = await readLeagueFile(picked);
    if (!read.ok) {
      tell(read.message, 'bad');
      return;
    }
    const own =
      read.file.team === null ? null : read.file.league.teams.find((t) => t.key === read.file.team);
    if (!own) {
      tell("That is a league's file. Choose a team's file to fill in a team.", 'bad');
      return;
    }
    setName(own.name.slice(0, 40));
    if (own.colors) choose(own.colors);
    const fileBadge = badgeFromFile(own.badge);
    if (fileBadge !== null) setBadge(fileBadge);
    tell(`Filled in from ${own.name}'s file. Save to keep it.`, 'good');
  };
  const type = (field: keyof TeamColors, raw: string) => {
    setCustom((c) => ({ ...c, [field]: raw }));
    const hex = hexOf(raw);
    // The preview follows the field whenever it holds a color.
    if (hex !== null) setColors((c) => ({ ...c, [field]: hex }));
  };
  const primaryError = hexOf(custom.primary) === null ? HEX_HINT : null;
  const secondaryError = hexOf(custom.secondary) === null ? HEX_HINT : null;
  const customInvalid = primaryError !== null || secondaryError !== null;

  const trimmed = name.trim();
  // The join's rule, said the way the server says it.
  const nameError = trimmed.length < 2 ? 'Two letters or more.' : null;
  const nameChanged = nameError === null && trimmed !== here.team.name;
  const colorsChanged = !sameColors(colors, here.team.colors);
  // THE LETTERS THE SERVER WILL STORE, from the same function it uses.
  const letters = nameError === null ? teamAbbreviation(trimmed) : here.team.abbreviation;
  const house = HOUSE_COLORS.findIndex((h) => sameColors(h, colors));
  const pro =
    sport?.proTeams.find((t) =>
      sameColors({ primary: t.palette.primary, secondary: t.palette.secondary }, colors),
    )?.id ?? null;
  const customChosen = house === -1 && pro === null;
  const blocked = saving || nameError !== null || customInvalid;

  const changed = nameChanged || colorsChanged || badgeChanged || bannerChanged;
  const submit = () => {
    if (blocked || !changed) return;
    setSaving(true);
    setError(null);
    void (async () => {
      const refused = await save(here, {
        ...(nameChanged ? { name: trimmed } : {}),
        ...(colorsChanged ? { colors } : {}),
        ...(badgeChanged ? { badge } : {}),
        ...(bannerChanged ? { banner } : {}),
      });
      setSaving(false);
      if (refused !== null) {
        setError(refused);
        return;
      }
      tell('Saved.', 'good');
      onClose();
    })();
  };

  return (
    <DetailSheet opened onClose={onClose} title="Edit team" closeOnEscape={!picking && !making}>
      <Stack gap={4}>
        <Colored
          className={`fsp-team-preview${hasBanner({ colors, badge, banner }) ? ' fsp-has-banner' : ''}`}
          role="group"
          aria-label="How your team will look"
          colors={{ '--team': colors.primary }}
        >
          <TeamBanner team={{ colors, badge, banner }} />
          <TeamMark team={{ abbreviation: letters, colors, badge }} size={44} />
          <div className="fsp-team-preview-body">
            <div className="fsp-team-preview-name">
              {trimmed.length > 0 ? trimmed : here.team.name}
            </div>
            <div className="fsp-sub">
              {here.team.ownerName} · {here.leagueName}
            </div>
          </div>
          {/* THE WHOLE CHARACTER beside the badge, its jersey and number where
              the team is named (§4). */}
          {teamFace({ abbreviation: letters, colors, badge }) !== null ? (
            <TeamFigure
              team={{ abbreviation: letters, colors, badge }}
              height={88}
              className="fsp-team-preview-figure"
            />
          ) : null}
        </Colored>

        <TextField
          label="Team name"
          value={name}
          onChange={setName}
          maxLength={40}
          error={nameError}
        />

        {here.source === 'api' ? (
          <BadgePicker
            letters={letters}
            colors={colors}
            value={badge}
            onChange={setBadge}
            onMakeFace={() => setMaking(true)}
          />
        ) : null}

        {here.source === 'api' ? (
          <BannerPicker
            colors={colors}
            value={banner}
            kept={kept}
            earned={earned}
            onChange={setBanner}
          />
        ) : null}

        {here.source === 'api' ? (
          <div>
            <input
              ref={fileInput}
              type="file"
              accept=".json,application/json"
              hidden
              onChange={(e) => {
                void fromFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <Button
              variant="quiet"
              size="xs"
              icon="upload"
              onClick={() => fileInput.current?.click()}
            >
              Fill in from a team’s file
            </Button>
          </div>
        ) : null}

        <div>
          <div className="fsp-field-label" id="fsp-house-colors-label">
            House colors
          </div>
          <div className="fsp-swatches" role="group" aria-labelledby="fsp-house-colors-label">
            {HOUSE_COLORS.map((h, i) => (
              <button
                key={h.name}
                type="button"
                className="fsp-swatch"
                // THE WORDS ON IT, FIRST (WCAG 2.5.3): the letters the swatch
                // shows, then the pair, so a voice that says what it reads
                // finds it.
                aria-label={badge === null ? `${letters} in ${h.name.toLowerCase()}` : h.name}
                aria-pressed={i === house}
                title={h.name}
                onClick={() => choose({ primary: h.primary, secondary: h.secondary })}
              >
                <TeamMark team={{ abbreviation: letters, colors: h, badge }} size={34} />
                {i === house ? (
                  <span className="fsp-swatch-check" aria-hidden>
                    <Icon name="accept" size="sm" />
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </div>

        {sport ? (
          <Choice
            label="Match a pro team"
            placeholder="Pick a team"
            value={pro}
            onChange={(id) => {
              const team = sport.proTeams.find((t) => t.id === id);
              if (team)
                choose({ primary: team.palette.primary, secondary: team.palette.secondary });
            }}
            options={sport.proTeams.map((t) => ({ value: t.id, label: t.name }))}
            required
          />
        ) : null}

        <div
          className="fsp-custom-colors"
          role="group"
          aria-labelledby="fsp-custom-colors-label"
          data-chosen={customChosen || undefined}
        >
          <Line spread gap={1}>
            <div className="fsp-field-label" id="fsp-custom-colors-label">
              Custom
            </div>
            {customChosen ? (
              <span className="fsp-custom-chosen">
                <Icon name="accept" size="sm" />
                In use
              </span>
            ) : null}
          </Line>
          <div className="fsp-custom-fields">
            <ColorField
              label="Badge"
              onPicking={setPicking}
              value={custom.primary}
              onChange={(v) => type('primary', v)}
              error={primaryError}
            />
            <ColorField
              label="Ring"
              onPicking={setPicking}
              value={custom.secondary}
              onChange={(v) => type('secondary', v)}
              error={secondaryError}
            />
          </div>
        </div>

        {error ? (
          <div role="alert">
            <Text tone="bad">{error}</Text>
          </div>
        ) : null}

        <Button
          variant="primary"
          full
          size="md"
          onClick={submit}
          loading={saving}
          disabled={blocked || !changed}
        >
          Save
        </Button>

        {here.source === 'api' ? (
          <FaceMaker
            opened={making}
            start={badge?.kind === 'face' ? badge.face : null}
            colors={colors}
            teamName={trimmed.length > 0 ? trimmed : here.team.name}
            others={others}
            onDone={(face, painted) => {
              const made: ChosenBadge = { kind: 'face', face };
              setBadge(made);
              if (!sameColors(painted, colors)) choose(painted);
              setMaking(false);
              // DONE KEEPS IT (B-488): a face is minutes of work, and a sheet
              // closed after Done, by Escape or Back, has no Save to ask
              // about. The face and the disc's colors go now; a name typed
              // in the sheet waits for its own Save.
              setSaving(true);
              setError(null);
              void (async () => {
                const refused = await save(here, {
                  badge: made,
                  ...(sameColors(painted, here.team.colors) ? {} : { colors: painted }),
                });
                setSaving(false);
                if (refused !== null) setError(refused);
                else tell('Your face is on your team.', 'good');
              })();
            }}
            onClose={() => setMaking(false)}
          />
        ) : null}

        {/* WHO RUNS IT (B13): a real league's co-managers, and the link to add one. */}
        {here.source === 'api' ? (
          <CoManagers leagueId={here.leagueId} teamId={here.team.id} onLeft={onClose} />
        ) : null}
      </Stack>
    </DetailSheet>
  );
}
